import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { CatalogList } from 'Services/ProductManagement/Features/Catalog/Components/CatalogList'
import { useCatalogsWithParams } from 'Services/ProductManagement/Features/Catalog/Hooks'
import TariffCatalogDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogDrawer'
import useSingleTariff from 'Services/ProductManagement/Features/Tariff/Hooks/useSingleTariff'
import {
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import AddIcon from '@mui/icons-material/Add'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

export const DRAWER_TAB_PARAM_KEY = 'drawerTab'

function TariffCatalogListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()

  const { singleTariff, singleLoading: singleTariffLoading } = useSingleTariff(tariffId)
  const {
    catalogs,
    isFetching: isCatalogsFetching,
    params,
    setParams,
    totalResults,
    selectedCatalogId,
    setSelectedCatalogId
  } = useCatalogsWithParams()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedCatalogId)
  const [searchText, setSearchText] = useState(params.search_text ?? '')

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: singleTariff ? singleTariff.description : ''
    },
    {
      title: t`Tariff sales channels`
    }
  ]

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleDrawerClose = () => {
    setSelectedCatalogId('')
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedCatalogId(id)
    setIsDrawerOpen(true)
  }

  function getTariffProductCountPerCatalog(catalogProductIds: string[]) {
    if (!singleTariff) return '-'
    // Get the number of tariff's products that are in the catalog's products
    return singleTariff.product_ids.filter(product_id => catalogProductIds.includes(product_id))
      .length
  }

  useEffect(() => {
    setParams(prevParams => ({ ...prevParams, tariff_id: tariffId }))
  }, [tariffId])

  const isSingleTariffLoading = singleTariffLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Tariff sales channels`}</PageTitle>
      </Box>
      <ListActions>
        <CustomButton
          onClick={() => navigate(`${baseUrl}/${tariffId}/sales-channels/create/`)}
          size="small"
          startIcon={<AddIcon />}
        >
          {t`Create new sales channel`}
        </CustomButton>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
          sx={{ marginLeft: 'auto' }}
        />
      </ListActions>
      <Box pt={1}>
        <CatalogList
          rows={catalogs}
          isLoading={isCatalogsFetching || isSingleTariffLoading}
          params={params}
          handleRequestSort={handleRequestSort}
          handleChangePage={handleChangePage}
          getProductCount={getTariffProductCountPerCatalog}
          selectedCatalogId={selectedCatalogId}
          onRowClick={handleRowClick}
          count={totalResults}
          page={
            params.start_index ? Number.parseInt(params.start_index, 10) / ROWS_PER_PAGE_DEFAULT : 0
          }
        />
      </Box>
      <TariffCatalogDrawer
        open={isDrawerOpen}
        handleClose={handleDrawerClose}
        getProductCount={getTariffProductCountPerCatalog}
        selectedCatalogId={selectedCatalogId}
      />
    </Box>
  )
}

export default TariffCatalogListPage
